/* ============================================================
   The ZERO Period — registration controller

   On submit: validate -> add the ticket variant to the cart with every
   answer attached as a line-item property -> go to checkout.

   The answers ride on the order itself, so they show up on the order in
   admin, in exports and on packing slips. GoKwik preserves line-item
   properties on this store (confirmed on live orders), so they survive
   its checkout too.
   ============================================================ */
(function () {
  'use strict';

  function init(root) {
    var cfgEl = root.querySelector('[data-zp-config]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

    var form = root.querySelector('#zpForm');
    var btn = root.querySelector('#zpBtn');
    var slip = root.querySelector('#zpSlip');
    if (!form || !btn) return;

    var busy = false;
    var defaultLabel = btn.textContent;

    function fld(id) { return root.querySelector('#' + id); }
    function wrap(id) { return root.querySelector('#' + id); }
    function val(id) { var el = fld(id); return el ? el.value.trim() : ''; }
    function mark(id, bad) {
      var el = wrap(id);
      if (el) el.classList.toggle('is-bad', bad);
    }

    ['zpName', 'zpPhone', 'zpEmail', 'zpSocial', 'zpBring'].forEach(function (id) {
      var el = fld(id);
      if (!el) return;
      el.addEventListener('input', function () {
        var f = this.closest('.zp-field');
        if (f) f.classList.remove('is-bad');
      });
    });

    function validate() {
      var ok = true;

      var name = val('zpName');
      mark('f-name', !name);
      if (!name) ok = false;

      var phone = val('zpPhone').replace(/[^0-9]/g, '');
      var pOk = phone.length >= 10 && phone.length <= 13;
      mark('f-phone', !pOk);
      if (!pOk) ok = false;

      var email = val('zpEmail');
      var eOk = email === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      mark('f-email', !eOk);
      if (!eOk) ok = false;

      var social = val('zpSocial');
      mark('f-social', !social);
      if (!social) ok = false;

      var bring = val('zpBring');
      mark('f-bring', !bring);
      if (!bring) ok = false;

      return ok;
    }

    function focusFirstError() {
      var first = slip ? slip.querySelector('.zp-field.is-bad') : null;
      if (!first) return;
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var f = first.querySelector('input, textarea');
      if (f) f.focus({ preventScroll: true });
    }

    function fail(msg) {
      busy = false;
      btn.disabled = false;
      btn.textContent = defaultLabel;
      alert(msg || "That didn't go through. Try once more, or message us at help@goatlife.co.in");
    }

    /* The form deliberately stays open even when stock has run out — the page
       never turns anyone away by itself. If Shopify then refuses the add, we
       surface this inline notice rather than an alert. */
    function showSoldOutNotice() {
      var s = root.querySelector('[data-zp-soldout]');
      if (!s) return;
      s.hidden = false;
      s.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* ---- checkout hand-off ----------------------------------------------
       GoKwik does NOT intercept /checkout. It binds to its own button and
       exposes onCheckoutClick() once merchant.integration.js has loaded
       (signalled by the 'gokwikLoaded' event). Sending the shopper to
       /checkout would drop them into Shopify's native checkout instead.

       So: refresh window.cart (GoKwik reads it, and ours is stale after the
       AJAX add), then click GoKwik's real button. Fall back to Shopify
       checkout only if GoKwik is switched off or never loads.
    ---------------------------------------------------------------------- */
    function gokwikButton() {
      var host = root.querySelector('[data-zp-gokwik-host]');
      return host ? host.querySelector('.gokwik-checkout button') : null;
    }

    function gokwikReady() {
      return typeof window.onCheckoutClick === 'function' || !!gokwikButton();
    }

    function fireGokwik() {
      var b = gokwikButton();
      if (b) {
        b.disabled = false;
        b.classList.remove('disabled');
        b.click();
        return true;
      }
      if (typeof window.onCheckoutClick === 'function') {
        window.onCheckoutClick(btn);
        return true;
      }
      return false;
    }

    function nativeCheckout() {
      window.location.href = cfg.checkoutUrl || '/checkout';
    }

    function goToCheckout() {
      if (!cfg.gokwikEnabled) { nativeCheckout(); return; }

      // GoKwik reads window.cart; ours is stale now that we've added a line.
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { window.cart = c; })
        .catch(function () { /* non-fatal */ })
        .then(function () {
          if (gokwikReady() && fireGokwik()) return;

          // Not loaded yet — wait for its ready event, with a hard timeout so
          // the shopper is never stranded on a spinning button.
          var done = false;
          function attempt() {
            if (done) return;
            if (fireGokwik()) { done = true; }
          }
          window.addEventListener('gokwikLoaded', attempt, { once: true });
          var tries = 0;
          var poll = setInterval(function () {
            tries++;
            if (done) { clearInterval(poll); return; }
            if (gokwikReady()) { attempt(); clearInterval(poll); return; }
            if (tries > 20) { // ~5s
              clearInterval(poll);
              if (!done) { done = true; nativeCheckout(); }
            }
          }, 250);
        });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;

      if (!cfg.variantId) {
        fail('Registration is not configured yet. Please contact us and we will sort your seat out.');
        return;
      }
      if (!validate()) { focusFirstError(); return; }

      busy = true;
      btn.disabled = true;
      btn.textContent = 'Holding your seat…';

      // Visible properties become the order's line-item details in admin.
      var props = {
        'Name': val('zpName'),
        'Phone': val('zpPhone'),
        'Instagram / LinkedIn': val('zpSocial'),
        'Bringing': val('zpBring'),
        'Bringing a pet': fld('zpPet') && fld('zpPet').checked ? 'Yes' : 'No'
      };
      var email = val('zpEmail');
      if (email) props['Email'] = email;

      var addUrl = (cfg.cartAddUrl || '/cart/add') + '.js';

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: cfg.variantId, quantity: 1, properties: props }]
        })
      })
        .then(function (r) {
          return r.json().then(function (d) { return { ok: r.ok, data: d }; });
        })
        .then(function (res) {
          if (!res.ok) {
            var d = res.data || {};
            // Shopify returns this when inventory has run out between page load and submit.
            var msg = d.description || d.message || '';
            if (/sold out|not enough|unavailable|inventory/i.test(msg)) {
              showSoldOutNotice();
              busy = false;
              btn.disabled = false;
              btn.textContent = defaultLabel;
            } else {
              fail(msg || null);
            }
            return;
          }
          goToCheckout();
        })
        .catch(function () { fail(null); });
    });
  }

  function boot() {
    document.querySelectorAll('[data-zp-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-render
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target.querySelector ? e.target.querySelector('[data-zp-root]') : null;
    if (r) init(r);
  });
})();
